<template>
  <section id="projects" class="py-20">
    <div class="max-w-6xl mx-auto px-6">
      <div class="text-center mb-12">
        <h2 class="text-3xl md:text-4xl font-bold mb-4" :class="isDark ? 'text-white' : 'text-gray-800'">项目作品</h2>
        <div class="w-20 h-1 bg-gradient-to-r from-blue-600 to-purple-600 mx-auto"></div>
      </div>
      <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
        <div 
          v-for="project in projects" 
          :key="project.title" 
          class="rounded-xl overflow-hidden transition-all duration-300 group"
          :class="isDark ? 'bg-gray-700 hover:shadow-xl' : 'bg-white shadow-lg hover:shadow-xl'"
        >
          <div class="relative h-48 overflow-hidden">
            <div class="absolute inset-0 bg-gradient-to-br" :class="project.gradient"></div>
            <div class="absolute inset-0 flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" class="w-16 h-16 text-white/80" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
            <div class="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
              <a v-if="project.github" :href="project.github" target="_blank" class="px-4 py-2 bg-white text-gray-800 rounded-lg mr-2 hover:bg-gray-100 transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                </svg>
              </a>
              <a v-if="project.demo" :href="project.demo" target="_blank" class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </a>
            </div>
          </div>
          <div class="p-6">
            <h3 class="text-xl font-semibold mb-2" :class="isDark ? 'text-white' : 'text-gray-800'">{{ project.title }}</h3>
            <p class="text-sm mb-4" :class="isDark ? 'text-gray-400' : 'text-gray-500'">{{ project.description }}</p>
            <div class="flex flex-wrap gap-2">
              <span v-for="tag in project.tags" :key="tag" 
                    class="px-3 py-1 text-sm rounded-full"
                    :class="isDark ? 'bg-gray-600 text-gray-300' : 'bg-gray-100 text-gray-600'">
                {{ tag }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { useTheme } from '../composables/useTheme'

export default {
  name: 'Projects',
  setup() {
    const { isDark } = useTheme()
    return { isDark }
  },
  data() {
    return {
      projects: [
        {
          title: '电商管理系统',
          description: '基于Vue3和Node.js构建的企业级电商后台管理系统，支持商品管理、订单处理、数据分析等功能。',
          tags: ['Vue3', 'Node.js', 'MongoDB', 'Element Plus'],
          gradient: 'from-blue-500 to-purple-500',
          github: '#',
          demo: '#'
        },
        {
          title: '在线协作平台',
          description: '实时协作工具，支持多人同时编辑文档、实时聊天和任务管理功能。',
          tags: ['React', 'Socket.io', 'PostgreSQL', 'Tailwind CSS'],
          gradient: 'from-green-500 to-teal-500',
          github: '#',
          demo: '#'
        },
        {
          title: '数据可视化仪表盘',
          description: '交互式数据可视化平台，支持多种图表类型和自定义数据展示。',
          tags: ['Vue3', 'ECharts', 'Python', 'FastAPI'],
          gradient: 'from-orange-500 to-red-500',
          github: '#',
          demo: '#'
        },
        {
          title: '个人博客系统',
          description: '现代化博客平台，支持Markdown编辑、评论系统和SEO优化。',
          tags: ['Nuxt.js', 'TypeScript', 'MySQL', 'Vuetify'],
          gradient: 'from-purple-500 to-pink-500',
          github: '#',
          demo: '#'
        },
        {
          title: '任务管理应用',
          description: '高效的任务管理工具，支持任务分类、优先级设置和进度追踪。',
          tags: ['Vue3', 'TypeScript', 'LocalStorage', 'Ant Design'],
          gradient: 'from-cyan-500 to-blue-500',
          github: '#',
          demo: '#'
        },
        {
          title: 'API接口文档工具',
          description: '自动化API文档生成工具，支持Swagger规范和在线测试功能。',
          tags: ['React', 'Node.js', 'Express', 'Swagger'],
          gradient: 'from-indigo-500 to-purple-500',
          github: '#',
          demo: '#'
        }
      ]
    }
  }
}
</script>
